/**
 * Invitations API
 *
 * Handles all invitation-related API calls.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import type { InvitationResponse, SuccessResponse } from '../types';

export interface InviteListItem {
  id: string;
  orgId: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired';
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string | null;
  invitedBy: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export interface ListInvitationsResponse {
  invites: InviteListItem[];
  total: number;
}

export interface ResendInvitationResponse {
  success: boolean;
  emailSent: boolean;
  expiresAt: string;
}

export class InvitationsApi extends BaseApiClient {
  async listInvitations(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListInvitationsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    const url = query
      ? `${API_ENDPOINTS.INVITATIONS_LIST}?${query}`
      : API_ENDPOINTS.INVITATIONS_LIST;

    return this.request<ListInvitationsResponse>(url);
  }

  async revokeInvitation(id: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.INVITATIONS_REVOKE(id), {
      method: 'DELETE',
    });
  }

  async resendInvitation(id: string): Promise<ResendInvitationResponse> {
    return this.request<ResendInvitationResponse>(API_ENDPOINTS.INVITATIONS_RESEND(id), {
      method: 'POST',
    });
  }

  async getInvitationByToken(token: string): Promise<InvitationResponse> {
    return this.request<InvitationResponse>(API_ENDPOINTS.INVITATIONS_BY_TOKEN(token));
  }

  async acceptInvitation(token: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.INVITATIONS_ACCEPT(token), {
      method: 'POST',
    });
  }
}
